import React, { useState, useEffect, useReducer, useRef, useLayoutEffect, useCallback } from "react";

import { isSameDay, format } from "date-fns";
import ExactTime from "../ExactTime";
import TicketHistoryStatus from "../TicketHistoryStatus";
import { mergeTicketHistory, historyRefreshKey } from "../../utils/ticketHistory";
import { contactDisplayName } from "../../utils/contactPresentation";
import { toDate } from "../../utils/waitingTime";
import { messageBelongsToTicket, messageTimelineReducer } from "../../utils/messageTimeline";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import PixMessageCard, { isPixMessage } from "../PixMessageCard";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundColor: theme.custom.chatBg,
    backgroundImage:
      theme.palette.type === "dark" ? "none" : `url(${whatsBackground})`,
    backgroundBlendMode: "overlay",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 156,
    maxWidth: "min(600px, 90%)",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.custom.bubbleIn,
    color: theme.palette.text.primary,
    alignSelf: "flex-start",
    borderTopLeftRadius: 4,
    borderTopRightRadius: 14,
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 6,
    paddingBottom: 0,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 2px rgba(0,0,0,.4)"
        : "0 1px 1px rgba(15,23,42,.12)",
  },

  quotedContainerLeft: {
    margin: "0 0 6px",
    overflow: "hidden",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(148,163,184,.1)" : "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 156,
    maxWidth: "min(600px, 90%)",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.custom.bubbleOut,
    color: theme.palette.type === "dark" ? "#E7F8F1" : "#0B3D2E",
    alignSelf: "flex-end",
    borderTopLeftRadius: 14,
    borderTopRightRadius: 4,
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 6,
    paddingBottom: 0,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 2px rgba(0,0,0,.4)"
        : "0 1px 1px rgba(15,23,42,.12)",
  },

  quotedContainerRight: {
    margin: "0 0 6px",
    overflowY: "hidden",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(5,150,105,.18)" : "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.text.secondary,
    overflowWrap: "break-word",
    padding: "3px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    maxWidth: "100%",
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: 7,
    gap: 2,
    lineHeight: 1.5,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    color: theme.palette.type === "dark" ? "#B4C4CB" : "#52676A",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: theme.palette.type === "dark" ? "#273B48" : "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: theme.palette.text.secondary,
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },
}));

const MessagesList = ({ ticketId, isGroup, canMarkRead = false }) => {
  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(messageTimelineReducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  const [history, setHistory] = useState(null);
  const [refreshingHistory, setRefreshingHistory] = useState(false);
  const [historyRevision, setHistoryRevision] = useState(0);
  const historyRef = useRef(null);
  const completedHistoryRef = useRef("");
  const markedReadRef = useRef(null);
  const initialScrollRef = useRef(false);
  const refreshInFlight = useRef(false);

  useLayoutEffect(() => {
    currentTicketId.current = ticketId;
    historyRef.current = null;
    completedHistoryRef.current = "";
    markedReadRef.current = null;
    initialScrollRef.current = false;
    refreshInFlight.current = false;
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHasMore(false);
    setHistory(null);
    setHistoryRevision(0);
    setRefreshingHistory(false);
    setAnchorEl(null);
  }, [ticketId]);

  const applyHistory = useCallback(incoming => {
    const next = mergeTicketHistory(historyRef.current, incoming);
    if (next === historyRef.current) return false;
    historyRef.current = next;
    setHistory(next);
    return true;
  }, []);

  const receiveHistory = useCallback(incoming => {
    if (!applyHistory(incoming) || incoming.status === "syncing") return;
    const key = historyRefreshKey(incoming);
    if (completedHistoryRef.current === key) return;
    completedHistoryRef.current = key;
    setPageNumber(1);
    setHistoryRevision(value => value + 1);
  }, [applyHistory]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const delayDebounceFn = setTimeout(async () => {
      try {
        const markRead = canMarkRead && pageNumber === 1 && markedReadRef.current !== ticketId;
        if (markRead) markedReadRef.current = ticketId;
        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber, ...(markRead ? { markRead: true } : {}) },
        });
        if (!active || currentTicketId.current !== ticketId) return;
        dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
        applyHistory(data.history);
        setHasMore(data.hasMore);
        setLoading(false);
        if (pageNumber === 1 && !initialScrollRef.current) {
          initialScrollRef.current = true;
          scrollToBottom();
        }
      } catch (err) {
        if (active && currentTicketId.current === ticketId) {
          setLoading(false);
          toastError(err);
        }
      }
    }, 500);
    return () => { active = false; clearTimeout(delayDebounceFn); };
  }, [pageNumber, ticketId, canMarkRead, historyRevision, applyHistory]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("connect", () => socket.emit("joinChatBox", ticketId));
    socket.on("appMessage", data => {
      if (currentTicketId.current !== ticketId || !messageBelongsToTicket(data.message, ticketId)) return;
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }
      if (data.action === "update") dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
    });
    socket.on("ticketHistory", data => {
      if (currentTicketId.current === ticketId && String(data.ticketId) === String(ticketId)) receiveHistory(data.history);
    });
    return () => socket.disconnect();
  }, [ticketId, receiveHistory]);

  // Recover a completed sync if its socket event was missed during a reconnect.
  useEffect(() => {
    if (history?.status !== "syncing") return undefined;
    let active = true;
    let pending = false;
    const timer = setInterval(async () => {
      if (pending) return;
      pending = true;
      try {
        const { data } = await api.get("/tickets/" + ticketId + "/history");
        if (active && currentTicketId.current === ticketId) receiveHistory(data.history);
      } catch (_) {
        // The last known status remains visible; the next tick retries the read.
      } finally { pending = false; }
    }, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [ticketId, history?.status, receiveHistory]);

  const handleRefreshHistory = async () => {
    if (refreshInFlight.current || historyRef.current?.status === "syncing") return;
    refreshInFlight.current = true;
    setRefreshingHistory(true);
    const requestedTicketId = ticketId;
    try {
      const { data } = await api.post("/tickets/" + requestedTicketId + "/history/sync", { force: true });
      if (currentTicketId.current === requestedTicketId) receiveHistory(data.history);
    } catch (err) {
      if (currentTicketId.current === requestedTicketId) toastError(err);
    } finally {
      if (currentTicketId.current === requestedTicketId) {
        refreshInFlight.current = false;
        setRefreshingHistory(false);
      }
    }
  };

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = () => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if ( /^.*\.(jpe?g|png|gif)?$/i.exec(message.mediaUrl) && message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} />
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
            >
              Download
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    const day = toDate(message.sentAt || message.createdAt);
    const previous = index > 0 ? messagesList[index - 1] : null;
    const previousDay = previous ? toDate(previous.sentAt || previous.createdAt) : null;
    if (!day || (previousDay && isSameDay(day, previousDay))) return null;
    return <div className={classes.dailyTimestamp} key={"timestamp-" + message.id}>
      <div className={classes.dailyTimestampText}><time dateTime={format(day, "yyyy-MM-dd")}>{format(day, "dd/MM/yyyy")}</time></div>
    </div>;
  };

  const renderMessageTimestamp = message => <span className={classes.timestamp}>
    <ExactTime value={message.sentAt || message.createdAt} label={message.sentAt ? "Horário de envio da mensagem" : "Horário registrado (histórico sem data de envio)"} />
    {message.fromMe && renderMessageAck(message)}
  </span>;

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {contactDisplayName(message.quotedMsg?.contact)}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageLeft}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {contactDisplayName(message.contact)}
                  </span>
                )}
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {isPixMessage(message.body) ? (
                    <PixMessageCard body={message.body} />
                  ) : (
                    <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  )}
                  {renderMessageTimestamp(message)}
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageRight}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {isPixMessage(message.body) ? (
                    <PixMessageCard body={message.body} />
                  ) : (
                    <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  )}
                  {renderMessageTimestamp(message)}
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <TicketHistoryStatus history={history} refreshing={refreshingHistory} onRefresh={handleRefreshHistory} />
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
        <div ref={lastMessageRef} aria-hidden="true" />
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;